/*
 * <ship-countdown> — product page shipping cutoff countdown
 * Arktic Studio
 *
 * Renders inside the inventory notice block, on the same line as the stock note.
 * Counts down to the next dispatch cutoff and states which day the order ships.
 *
 * The cutoff is FIXED at 12:00 Quebec time and all date maths are pinned to that
 * zone, never the visitor's — Paris and Montreal see the same deadline, and it
 * stays correct across DST.
 *
 * Schedule:
 *   deadline = today's cutoff if now < cutoff, otherwise tomorrow's cutoff
 *   ship day = today, if today is a shipping day AND now < cutoff
 *              otherwise the next shipping day after today
 *
 *   Mon 09:00 -> counts to Mon 12:00, "aujourd'hui"
 *   Mon 14:00 -> counts to Tue 12:00, "demain"
 *   Fri 14:00 -> counts to Sat 12:00, "lundi"
 *   Sun 00:00 -> counts to Sun 12:00, "demain"
 *
 * Stock sync: mirrors the sibling .stock-note. A normal variant change
 * re-renders #inventory-notice-{id} wholesale, but setUnavailable() only
 * patches classes in place — the MutationObserver catches that path.
 *
 * Copy: data-text carries one sentence with {timer} and {day} placeholders.
 * Both are wrapped in their own span so they can be styled independently.
 */
(function () {
  if (window.customElements && customElements.get('ship-countdown')) return;

  // 'America/Toronto' is the canonical IANA zone for Quebec — 'America/Montreal'
  // is a deprecated alias and some browsers no longer resolve it.
  var TIMEZONE = 'America/Toronto';
  var CUTOFF_HOUR = 12;
  var CUTOFF_MINUTE = 0;

  // Days the warehouse ships. 0 = Sunday ... 6 = Saturday.
  // Add 6 here if Saturday dispatch ever starts.
  var SHIPPING_DAYS = [1, 2, 3, 4, 5];

  var SOLD_OUT_CLASS = 'stock-note--sold';

  /* ---------- timezone helpers ---------- */

  // Wall-clock parts of `date` as seen in `tz`.
  function partsIn(tz, date) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      // h23 explicitly: hour12:false resolves to the h24 cycle in some engines,
      // which reports midnight as hour 24 — and in a few WebKit builds pairs
      // that with the PREVIOUS date, shifting `today` back a day at 00:00.
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    var out = {};
    dtf.formatToParts(date).forEach(function (p) {
      out[p.type] = p.value;
    });
    var hour = parseInt(out.hour, 10);
    if (hour === 24) hour = 0;
    return {
      year: parseInt(out.year, 10),
      month: parseInt(out.month, 10),
      day: parseInt(out.day, 10),
      hour: hour,
      minute: parseInt(out.minute, 10),
      second: parseInt(out.second, 10)
    };
  }

  // Offset of `tz` at `date`, in ms (local minus UTC).
  function offsetMs(tz, date) {
    var p = partsIn(tz, date);
    var asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUTC - Math.floor(date.getTime() / 1000) * 1000;
  }

  // Real UTC timestamp for a wall-clock time in `tz`. Two passes so DST
  // transition days resolve correctly.
  function utcFor(tz, y, m, d, h, min) {
    var guess = Date.UTC(y, m - 1, d, h, min, 0);
    var ts = guess - offsetMs(tz, new Date(guess));
    return guess - offsetMs(tz, new Date(ts));
  }

  /* ---------- civil date helpers (UTC-anchored, no time component) ---------- */

  function civil(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d));
  }

  function addDays(c, n) {
    return new Date(c.getTime() + n * 86400000);
  }

  function iso(c) {
    return (
      c.getUTCFullYear() +
      '-' +
      ('0' + (c.getUTCMonth() + 1)).slice(-2) +
      '-' +
      ('0' + c.getUTCDate()).slice(-2)
    );
  }

  function isShippingDay(c, holidays) {
    return SHIPPING_DAYS.indexOf(c.getUTCDay()) !== -1 && holidays.indexOf(iso(c)) === -1;
  }

  function nextShippingDay(c, holidays) {
    var d = addDays(c, 1);
    for (var i = 0; i < 30; i++) {
      if (isShippingDay(d, holidays)) return d;
      d = addDays(d, 1);
    }
    return d;
  }

  /* ---------- formatting ---------- */

  function formatTimer(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    var h = Math.floor(s / 3600);
    s -= h * 3600;
    var m = Math.floor(s / 60);
    s -= m * 60;
    return ('0' + h).slice(-2) + ':' + ('0' + m).slice(-2) + ':' + ('0' + s).slice(-2);
  }

  function weekdayName(locale, civilDate) {
    try {
      return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(civilDate);
    } catch (e) {
      return new Intl.DateTimeFormat('fr', { weekday: 'long', timeZone: 'UTC' }).format(civilDate);
    }
  }

  /* ---------- element ---------- */

  var ShipCountdown = function () {
    return Reflect.construct(HTMLElement, [], ShipCountdown);
  };
  ShipCountdown.prototype = Object.create(HTMLElement.prototype);
  ShipCountdown.prototype.constructor = ShipCountdown;
  Object.setPrototypeOf(ShipCountdown, HTMLElement);

  ShipCountdown.prototype.connectedCallback = function () {
    this.textEl = this.querySelector('.ship-countdown__text');
    if (!this.textEl) return;

    this.locale = this.dataset.locale || document.documentElement.lang || 'fr';

    this.template = this.dataset.text || '';
    this.words = {
      today: this.dataset.wordToday || '',
      tomorrow: this.dataset.wordTomorrow || ''
    };

    this.holidays = (this.dataset.holidays || '')
      .split(/[\s,;]+/)
      .map(function (v) {
        return v.trim();
      })
      .filter(function (v) {
        return /^\d{4}-\d{2}-\d{2}$/.test(v);
      });

    // Bail out silently if the browser can't do timezone maths — better a
    // missing line than a wrong shipping promise.
    try {
      partsIn(TIMEZONE, new Date());
    } catch (e) {
      return;
    }

    var self = this;
    this.state = null;

    this.stockEl = this.parentElement ? this.parentElement.querySelector('.stock-note') : null;
    if (this.stockEl) {
      this.stockObserver = new MutationObserver(function () {
        self.render();
      });
      this.stockObserver.observe(this.stockEl, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    this.render();

    this.timer = setInterval(function () {
      self.render();
    }, 1000);

    this.onVisibility = function () {
      if (!document.hidden) self.render();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  };

  ShipCountdown.prototype.disconnectedCallback = function () {
    if (this.timer) clearInterval(this.timer);
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.stockObserver) this.stockObserver.disconnect();
  };

  ShipCountdown.prototype.isSoldOut = function () {
    return !!this.stockEl && this.stockEl.classList.contains(SOLD_OUT_CLASS);
  };

  ShipCountdown.prototype.compute = function (now) {
    var p = partsIn(TIMEZONE, now);
    var today = civil(p.year, p.month, p.day);
    var beforeCutoff = p.hour * 60 + p.minute < CUTOFF_HOUR * 60 + CUTOFF_MINUTE;

    var deadlineDay = beforeCutoff ? today : addDays(today, 1);
    var deadline = utcFor(
      TIMEZONE,
      deadlineDay.getUTCFullYear(),
      deadlineDay.getUTCMonth() + 1,
      deadlineDay.getUTCDate(),
      CUTOFF_HOUR,
      CUTOFF_MINUTE
    );

    var shipDay =
      beforeCutoff && isShippingDay(today, this.holidays)
        ? today
        : nextShippingDay(today, this.holidays);

    var dayName;
    if (iso(shipDay) === iso(today)) dayName = this.words.today;
    else if (iso(shipDay) === iso(addDays(today, 1))) dayName = this.words.tomorrow;
    else dayName = weekdayName(this.locale, shipDay);

    return { deadline: deadline, dayName: dayName };
  };

  ShipCountdown.prototype.render = function () {
    if (this.isSoldOut() || !this.template) {
      this.hidden = true;
      return;
    }

    var now = new Date();
    var s = this.compute(now);

    // Rebuild the sentence only when the wording changes, then tick the timer.
    if (this.state !== s.dayName) {
      this.state = s.dayName;
      this.timerEl = null;
      this.textEl.textContent = '';

      var parts = this.template.split(/(\{timer\}|\{day\})/);
      for (var i = 0; i < parts.length; i++) {
        var part = parts[i];
        if (part === '{timer}') {
          this.timerEl = document.createElement('span');
          this.timerEl.className = 'ship-countdown__timer';
          this.timerEl.setAttribute('aria-live', 'off');
          this.textEl.appendChild(this.timerEl);
        } else if (part === '{day}') {
          var dayEl = document.createElement('span');
          dayEl.className = 'ship-countdown__day';
          dayEl.textContent = s.dayName;
          this.textEl.appendChild(dayEl);
        } else if (part) {
          this.textEl.appendChild(document.createTextNode(part));
        }
      }
    }

    if (this.timerEl) {
      this.timerEl.textContent = formatTimer(s.deadline - now.getTime());
    }

    this.hidden = false;
  };

  customElements.define('ship-countdown', ShipCountdown);
})();